import LZString from 'lz-string';
import type { GameState } from '../state';
import { saveBlobSchema, type SaveBlob } from './schema';
import { makeSaveBlob, deserializeBlob } from './serialize';

/**
 * Export/import codes. The export string is the player's backup AND the public,
 * parseable format for fan tools (TECHNICAL_APPROACH §10). A short prefix lets us
 * recognize and version the envelope independently of the payload.
 */
const PREFIX = 'BME1-';

export function exportCode(state: GameState, savedAt: number): string {
  const blob = makeSaveBlob(state, savedAt);
  const json = JSON.stringify(blob);
  return PREFIX + LZString.compressToBase64(json);
}

export class ImportError extends Error {}

export function importCode(code: string): GameState {
  const trimmed = code.trim();
  if (!trimmed.startsWith(PREFIX)) {
    throw new ImportError('Unrecognized save code (bad prefix).');
  }
  const payload = trimmed.slice(PREFIX.length);
  const json = LZString.decompressFromBase64(payload);
  if (!json) throw new ImportError('Could not decompress save code.');

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new ImportError('Save code is not valid JSON.');
  }

  const blobResult = saveBlobSchema.safeParse(raw);
  if (!blobResult.success) {
    throw new ImportError('Save code envelope is malformed.');
  }

  try {
    return deserializeBlob(blobResult.data as SaveBlob);
  } catch (e) {
    throw new ImportError(e instanceof Error ? e.message : 'Save data is invalid.');
  }
}

/** Serialize a blob to a compact JSON string for SaveStorage (no compression). */
export function blobToJson(blob: SaveBlob): string {
  return JSON.stringify(blob);
}

export function jsonToBlob(json: string): SaveBlob {
  const raw = JSON.parse(json);
  return saveBlobSchema.parse(raw) as SaveBlob;
}
